import React, {useState} from "react";
import {Box, Chip, Divider, Paper, Stack, Typography} from "@mui/material";
import Property from "crqlar-react-common/dist/components/Property";
import {addDays, formatFullDMY, startOfDay} from "crqlar-react-common/dist/utils/date.util";
import {useTranslation} from "react-i18next";
import SingleDatePicker from "../../common/newDatePicker/SingleDatePicker";
import RangeDatePicker from "../../common/newDatePicker/RangeDatePicker";
import {DateRange} from "../../common/newDatePicker/datePickerShared";

const SectionHeader = ({label}: {label: string}) => (
    <Typography
        variant="subtitle2"
        color="text.secondary"
        sx={{textTransform: "uppercase", letterSpacing: 0.6, fontWeight: 700, mb: 1}}
    >
        {label}
    </Typography>
);

const NewDatePickerTestPage = () => {
    const {t} = useTranslation();
    const today = startOfDay(new Date());

    const [date, setDate] = useState<Date>(today);
    const [range, setRange] = useState<DateRange>({start: today, end: addDays(today, 6)});

    const rangeLabel = (value: DateRange) => `${formatFullDMY(value.start)} – ${formatFullDMY(value.end)}`;

    // PageShell bounds the tab at 100dvh and clips it, so this page has to own its own scroller
    return (
        <Box sx={{flex: 1, minWidth: 0, minHeight: 0, overflowY: "auto"}}>
            <Box sx={{p: {xs: 2, sm: 3}, maxWidth: 920, mx: "auto"}}>
                <Typography variant="h5" sx={{fontWeight: 700}}>
                    Date pickers
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{mb: 3}}>
                    {t("Two dedicated pickers — SingleDatePicker and RangeDatePicker — headless triggers over a Mobiscroll calendar.")}
                </Typography>

                {/* ---------------- Single ---------------- */}
                <Paper variant="outlined" sx={{p: {xs: 2, sm: 3}, mb: 3, borderRadius: 2}}>
                    <SectionHeader label={t("SingleDatePicker")}/>

                    <Stack divider={<Divider flexItem/>} spacing={2}>
                        <Property title={t("Default")} subtitle={t("Weekday · steps · today")}>
                            <SingleDatePicker value={date} onChange={setDate}/>
                        </Property>

                        <Property title={t("No weekday")}>
                            <SingleDatePicker value={date} onChange={setDate} showWeekday={false}/>
                        </Property>

                        <Property title={t("No step buttons")}>
                            <SingleDatePicker value={date} onChange={setDate} showStepButtons={false}/>
                        </Property>

                        <Property title={t("No today button")}>
                            <SingleDatePicker value={date} onChange={setDate} showTodayButton={false}/>
                        </Property>

                        <Property title={t("Minimal")} subtitle={t("Trigger only")}>
                            <SingleDatePicker
                                value={date}
                                onChange={setDate}
                                showWeekday={false}
                                showStepButtons={false}
                                showTodayButton={false}
                            />
                        </Property>

                        <Property title={t("Disabled")}>
                            <SingleDatePicker value={date} onChange={setDate} disabled/>
                        </Property>
                    </Stack>

                    <Stack direction="row" alignItems="center" spacing={1} sx={{mt: 2.5}}>
                        <Typography variant="body2" color="text.secondary">
                            {t("Selected date")}:
                        </Typography>
                        <Chip size="small" label={formatFullDMY(date)}/>
                    </Stack>
                </Paper>

                {/* ---------------- Range ---------------- */}
                <Paper variant="outlined" sx={{p: {xs: 2, sm: 3}, borderRadius: 2}}>
                    <SectionHeader label={t("RangeDatePicker")}/>

                    <Stack divider={<Divider flexItem/>} spacing={2}>
                        <Property title={t("Default")} subtitle={t("Weekday · steps · today")}>
                            <RangeDatePicker value={range} onChange={setRange}/>
                        </Property>

                        <Property title={t("No weekday")}>
                            <RangeDatePicker value={range} onChange={setRange} showWeekday={false}/>
                        </Property>

                        <Property title={t("No step buttons")}>
                            <RangeDatePicker value={range} onChange={setRange} showStepButtons={false}/>
                        </Property>

                        <Property title={t("No today button")}>
                            <RangeDatePicker value={range} onChange={setRange} showTodayButton={false}/>
                        </Property>

                        <Property title={t("Disabled")}>
                            <RangeDatePicker value={range} onChange={setRange} disabled/>
                        </Property>
                    </Stack>

                    <Stack direction="row" alignItems="center" spacing={1} sx={{mt: 2.5}}>
                        <Typography variant="body2" color="text.secondary">
                            {t("Selected range")}:
                        </Typography>
                        <Chip size="small" label={rangeLabel(range)}/>
                    </Stack>
                </Paper>

                {/* ---------------- Mobile / inside layout ---------------- */}
                <Paper variant="outlined" sx={{p: {xs: 2, sm: 3}, mt: 3, borderRadius: 2}}>
                    <SectionHeader label={t("Step buttons inside (mobile layout)")}/>
                    <Typography variant="body2" color="text.secondary" sx={{mb: 2, mt: -0.5}}>
                        {t('Forced here with stepButtonsPlacement="inside". With the default "auto" this layout kicks in automatically below the sm breakpoint.')}
                    </Typography>

                    <Stack divider={<Divider flexItem/>} spacing={2}>
                        <Property title={t("Single")}>
                            <SingleDatePicker value={date} onChange={setDate} stepButtonsPlacement="inside"/>
                        </Property>

                        <Property title={t("Range")}>
                            <RangeDatePicker value={range} onChange={setRange} stepButtonsPlacement="inside"/>
                        </Property>

                        <Property title={t("Range · no today")}>
                            <RangeDatePicker
                                value={range}
                                onChange={setRange}
                                stepButtonsPlacement="inside"
                                showTodayButton={false}
                            />
                        </Property>
                    </Stack>
                </Paper>

                {/* ---------------- Sizes ---------------- */}
                <Paper variant="outlined" sx={{p: {xs: 2, sm: 3}, mt: 3, borderRadius: 2}}>
                    <SectionHeader label={t("Sizes")}/>
                    <Typography variant="body2" color="text.secondary" sx={{mb: 2, mt: -0.5}}>
                        {t('"medium" (40px, the default) matches a standard input; "small" (36px) for denser rows.')}
                    </Typography>

                    <Stack divider={<Divider flexItem/>} spacing={2}>
                        <Property title={t("Single · medium")} subtitle={t("40px")}>
                            <SingleDatePicker value={date} onChange={setDate}/>
                        </Property>

                        <Property title={t("Single · small")} subtitle={t("36px")}>
                            <SingleDatePicker value={date} onChange={setDate} size="small"/>
                        </Property>

                        <Property title={t("Range · medium")} subtitle={t("40px")}>
                            <RangeDatePicker value={range} onChange={setRange}/>
                        </Property>

                        <Property title={t("Range · small")} subtitle={t("36px")}>
                            <RangeDatePicker value={range} onChange={setRange} size="small"/>
                        </Property>
                    </Stack>
                </Paper>
            </Box>
        </Box>
    );
};

export default NewDatePickerTestPage;
